import { useEffect, useRef, useState } from "react";
import type {
  EnvironmentId,
  ProjectId,
  PublicContributionProject,
  PublicContributionProjectPage,
} from "@afar-research/contracts";
import { readLocalApi } from "../../localApi";
import { useConnectAccount } from "../../hooks/useConnectAccount";
import { randomUUID } from "../../lib/utils";
import { projectEnvironment } from "../../state/projects";
import { useEnvironmentQuery } from "../../state/query";
import { useAtomCommand } from "../../state/use-atom-command";
import { Button } from "../ui/button";
import { ProjectContributionSnapshotPreview } from "./ProjectContributionSnapshotPreview";

/** One publishing flow, shared by the Contributions tab and Project Settings. */
export function ProjectContributionPublishing(props: {
  readonly environmentId: EnvironmentId;
  readonly projectId: ProjectId;
}) {
  const { account, refresh: refreshAccount } = useConnectAccount();
  const bridge = readLocalApi()?.connect;
  const draft = useEnvironmentQuery(
    projectEnvironment.projectContributionGenerationEvents({
      environmentId: props.environmentId,
      input: { projectId: props.projectId },
    }),
  );
  const generate = useAtomCommand(projectEnvironment.generateProjectContribution);
  const cancel = useAtomCommand(projectEnvironment.cancelProjectContribution);
  const [branch, setBranch] = useState("HEAD");
  const [projects, setProjects] = useState<readonly PublicContributionProject[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [history, setHistory] = useState<PublicContributionProjectPage | null>(null);
  const [name, setName] = useState("");
  const [publisher, setPublisher] = useState("");
  const [repository, setRepository] = useState("");
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const creationRequest = useRef<{ key: string; id: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [publishedUrl, setPublishedUrl] = useState<string | null>(null);
  const [withdraw, setWithdraw] = useState<string | null>(null);
  const accountId = account?.state === "signed-in" ? account.activeAccountId : undefined;
  const selected = projects.find((project) => project.id === selectedId);

  useEffect(() => {
    let active = true;
    setProjects([]);
    setSelectedId("");
    setHistory(null);
    setPublishedUrl(null);
    if (accountId && bridge?.contributionProjects)
      void bridge.contributionProjects().then(
        (rows) => {
          if (active) setProjects(rows);
        },
        (cause) => {
          if (active)
            setError(
              cause instanceof Error ? cause.message : "Could not read your public projects.",
            );
        },
      );
    return () => {
      active = false;
    };
  }, [accountId, bridge]);
  useEffect(() => {
    let active = true;
    setHistory(null);
    setWithdraw(null);
    if (selectedId && bridge?.contributionProject)
      void bridge.contributionProject(selectedId).then(
        (page) => {
          if (active) setHistory(page);
        },
        (cause) => {
          if (active)
            setError(
              cause instanceof Error ? cause.message : "Could not read published snapshots.",
            );
        },
      );
    return () => {
      active = false;
    };
  }, [selectedId, bridge]);
  const run = async (operation: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await operation();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The request failed. Retry when the connection is available.",
      );
      refreshAccount();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  const generateDraft = () =>
    run(async () => {
      const result = await generate({
        environmentId: props.environmentId,
        input: { projectId: props.projectId, id: randomUUID(), branch: branch.trim() },
      });
      if (result._tag !== "Success")
        throw new Error(
          "Could not start generation. Check the selected branch and text-generation provider.",
        );
    });
  const publish = () =>
    run(async () => {
      const state = draft.data;
      if (
        !selected ||
        !state?.snapshot ||
        state.phase !== "ready" ||
        !bridge?.publishProjectContribution ||
        !accountId
      )
        throw new Error("Choose your public project and sign in before publishing.");
      await bridge.publishProjectContribution(selected.id, state.id, state.snapshot);
      setPublishedUrl(selected.publicUrl);
      if (bridge.contributionProject) setHistory(await bridge.contributionProject(selected.id));
    });

  return (
    <section className="space-y-3 rounded-lg border p-3 text-sm">
      <h2 className="font-medium">Publish project snapshot</h2>
      <p className="text-xs text-muted-foreground">
        Publish aggregate human and agent contributions, plus a summary of human ideas and guidance.
        Raw Agent Trace exports, file details and model breakdowns stay local.
      </p>
      <label className="block text-xs">
        Branch in the Primary Checkout
        <input
          className="mt-1 block w-full rounded border bg-background px-2 py-1.5 text-sm"
          value={branch}
          onChange={(event) => setBranch(event.target.value)}
          disabled={busy || draft.data?.phase === "generating"}
        />
      </label>
      <div className="flex flex-wrap gap-2">
        <Button
          size="xs"
          variant="outline"
          disabled={busy || !branch.trim() || draft.data?.phase === "generating"}
          onClick={() => void generateDraft()}
        >
          {draft.data ? "Regenerate summary" : "Generate summary"}
        </Button>
        {draft.data?.phase === "generating" || draft.data?.phase === "ready" ? (
          <Button
            size="xs"
            variant="outline"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const state = draft.data;
                if (!state) return;
                const result = await cancel({
                  environmentId: props.environmentId,
                  input: { projectId: props.projectId, id: state.id, revision: state.revision },
                });
                if (result._tag !== "Success")
                  throw new Error("The draft changed. Refresh and retry cancellation.");
              })
            }
          >
            {draft.data.phase === "ready" ? "Discard draft" : "Cancel generation"}
          </Button>
        ) : null}
      </div>
      {draft.data?.phase === "generating" ? (
        <p role="status" className="text-xs text-muted-foreground">
          Inspecting project history: {draft.data.inspectedPages} of {draft.data.requiredPages}{" "}
          pages. Generation continues if you disconnect.
        </p>
      ) : null}
      {draft.data?.phase === "failed" ? (
        <p role="alert" className="text-xs text-destructive">
          {draft.data.failure === "text_generation_provider_unavailable"
            ? "The configured text-generation provider is unavailable. Enable it in Provider Settings, then regenerate."
            : "Generation did not finish inspecting the complete history. Regenerate to retry."}{" "}
          Nothing was published.
        </p>
      ) : null}
      {draft.data?.phase === "cancelled" ? (
        <p className="text-xs text-muted-foreground">
          Generation cancelled. Nothing was published.
        </p>
      ) : null}
      {draft.error ? (
        <p role="alert" className="text-xs text-destructive">
          {draft.error}
        </p>
      ) : null}
      {!accountId ? (
        <p className="text-xs text-muted-foreground">
          Sign in to AFAR Connect in Account Settings to publish and manage public snapshots.
        </p>
      ) : (
        <>
          <label className="block text-xs">
            Public project
            <select
              aria-label="Public project"
              className="mt-1 block w-full rounded border bg-background p-2 text-sm"
              value={selectedId}
              disabled={busy}
              onChange={(event) => {
                setSelectedId(event.target.value);
                setPublishedUrl(null);
              }}
            >
              <option value="">Choose an existing public project…</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name} · {project.publisherName}
                </option>
              ))}
            </select>
          </label>
          <p className="text-xs text-muted-foreground">
            To publish the same project from another Environment, explicitly choose its existing
            public project here.
          </p>
          <details>
            <summary className="cursor-pointer text-xs">Create a public project</summary>
            <div className="mt-2 space-y-2">
              <label className="block text-xs">
                Public project name
                <input
                  aria-label="Public project name"
                  className="mt-1 block w-full rounded border bg-background p-2 text-sm"
                  value={name}
                  maxLength={160}
                  disabled={busy}
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
              <label className="block text-xs">
                Public publisher name
                <input
                  aria-label="Public publisher name"
                  className="mt-1 block w-full rounded border bg-background p-2 text-sm"
                  value={publisher}
                  maxLength={160}
                  disabled={busy}
                  onChange={(event) => setPublisher(event.target.value)}
                />
              </label>
              <label className="block text-xs">
                GitHub repository URL (optional)
                <input
                  aria-label="Public GitHub repository URL"
                  className="mt-1 block w-full rounded border bg-background p-2 text-sm"
                  value={repository}
                  disabled={busy}
                  onChange={(event) => setRepository(event.target.value)}
                />
              </label>
              <Button
                size="xs"
                variant="outline"
                disabled={busy || !name.trim() || !publisher.trim()}
                onClick={() =>
                  void run(async () => {
                    if (!bridge?.createContributionProject)
                      throw new Error("Public snapshot publishing is unavailable on this client.");
                    const key = JSON.stringify([
                      accountId,
                      name.trim(),
                      publisher.trim(),
                      repository.trim(),
                    ]);
                    if (creationRequest.current?.key !== key)
                      creationRequest.current = { key, id: randomUUID() };
                    const project = await bridge.createContributionProject({
                      creationId: creationRequest.current.id,
                      name: name.trim(),
                      publisherName: publisher.trim(),
                      repositoryUrl: repository.trim() || null,
                    });
                    setProjects((rows) => [
                      ...rows.filter((row) => row.id !== project.id),
                      project,
                    ]);
                    setSelectedId(project.id);
                  })
                }
              >
                Create public project
              </Button>
            </div>
          </details>
        </>
      )}
      {selected ? (
        <p className="text-xs">
          Publishing as <strong>{selected.publisherName}</strong> for{" "}
          <strong>{selected.name}</strong>.{" "}
          <a
            className="text-primary underline"
            href={selected.publicUrl}
            target="_blank"
            rel="noreferrer"
          >
            Public page
          </a>
        </p>
      ) : null}
      {draft.data?.phase === "ready" && draft.data.snapshot && selected && accountId ? (
        <ProjectContributionSnapshotPreview
          key={`${draft.data.id}:${selected.id}:${accountId}`}
          snapshot={draft.data.snapshot}
          publishing={busy}
          onPublish={publish}
        />
      ) : null}
      {draft.data?.phase === "ready" && !selected ? (
        <p className="text-xs text-muted-foreground">
          Your summary is ready. Choose a public project to preview and approve the exact snapshot.
        </p>
      ) : null}
      {publishedUrl ? (
        <p role="status" className="text-xs">
          Snapshot published.{" "}
          <a
            className="text-primary underline"
            href={publishedUrl}
            target="_blank"
            rel="noreferrer"
          >
            View the public project
          </a>
        </p>
      ) : null}
      {selected ? (
        <div className="space-y-2">
          <h3 className="text-xs font-medium">Published snapshots</h3>
          {history?.snapshots.map((snapshot) => (
            <div
              key={snapshot.id}
              className="flex flex-wrap items-center justify-between gap-2 text-xs"
            >
              <span>{new Date(snapshot.publishedAt).toLocaleString()}</span>
              <Button
                size="xs"
                variant="outline"
                disabled={busy}
                onClick={() => setWithdraw(snapshot.id)}
              >
                Withdraw snapshot
              </Button>
            </div>
          ))}
          <Button
            size="xs"
            variant="outline"
            disabled={busy}
            onClick={() => setWithdraw("project")}
          >
            Withdraw entire public project
          </Button>
        </div>
      ) : null}
      {withdraw && selected ? (
        <div className="space-y-2 rounded border border-destructive/40 p-2">
          <p className="text-xs">
            {withdraw === "project"
              ? "Remove this public project and all of its snapshots?"
              : "Remove this snapshot from public view? If it is the latest, the previous snapshot becomes current."}
          </p>
          <Button
            size="xs"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                if (!bridge?.withdrawProjectContribution) return;
                await bridge.withdrawProjectContribution(
                  selected.id,
                  withdraw === "project" ? undefined : withdraw,
                );
                setWithdraw(null);
                if (withdraw === "project") {
                  setProjects((rows) => rows.filter((project) => project.id !== selected.id));
                  setSelectedId("");
                  setPublishedUrl(null);
                } else if (bridge.contributionProject)
                  setHistory(await bridge.contributionProject(selected.id));
              })
            }
          >
            Confirm withdrawal
          </Button>
          <Button size="xs" variant="outline" disabled={busy} onClick={() => setWithdraw(null)}>
            Keep published
          </Button>
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </section>
  );
}
